<template>
  <div>
    <div class="mb-6 pb-6 border-b-2 border-grey-light flex flex-wrap items-center">
      <div>
        <h1 class="text-grey-darker text-xl font-semibold leading-normal uppercase">
          Custom Print Orders
        </h1>
        <a
          v-if="officeAccess"
          @click="$router.push('/print-order-form')"
          class="text-grey-dark text-sm font-medium leading-normal cursor-pointer no-underline hover:underline">
          &lt; Order Another Product
        </a>
      </div>
    </div>

    <loader v-if="!pageLoaded" />

    <!-- OFFICE HAS NO ACCESS -->
    <panel v-else-if="!officeAccess" class="pt-6">
      <strong class="mb-2">Access Denied</strong>
      <p>Your office does not currently have access to use the print order form.</p>
    </panel>

    <div v-else class="rounded-lg bg-white relative flex flex-col p-6">
      <h3 class="pb-6 leading-none"><i class="far fa-bookmark mr-2 text-lg" /> Saved Items</h3>
      <div class="flex flex-wrap items-center leading-none pb-6 border-b-2 border-grey-light">
        <div class="flex w-full mb-4 lg:w-1/3 lg:mb-0">
          <div class="flex flex-row w-full p-3 rounded border border-grey-light bg-white ">
            <input type="text" v-model="searchParams.q" placeholder="Search" class="flex-grow pr-3 border-r border-grey-light" />
            <i class="fa fa-search text-grey pl-3"></i>
          </div>
        </div>
        <div class="w-full lg:w-2/3">
          <print-sub-menu ref="printSubMenuRef" />
        </div>
      </div>

      <loader v-if="!searchLoaded" class="pt-6" />

      <div v-else-if="savedItems.length === 0" class="py-6">
        <p>There are no saved items to display. <a class="underline cursor-pointer" @click="$router.push('/print-order-form')">Continue shopping</a></p>
      </div>

      <table v-else class="w-full mb-8 leading-normal">
        <thead>
          <tr class="text-sm uppercase leading-tight">
            <th class="text-left font-semibold pl-6 pr-4 py-4">Item Description</th>
            <th class="text-center font-semibold p-4">Quantity</th>
            <th class="text-center font-semibold p-4">Cost</th>
            <th class="text-center font-semibold p-4">Saved</th>
            <th class="text-center font-semibold p-4">Edit</th>
            <th class="text-center font-semibold p-4">Delete</th>
            <th class="text-center font-semibold pl-4 pr-6 py-4">Order</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, i) in savedItems" :key="i" class="border-t border-b border-grey-light text-sm">
            <td class="pl-6 pr-4 py-4 border-l-8 border-grey-light">
              <p class="text-sm font-semibold">{{ item.sale_item_category.name + ' - ' + item.sale_item.name }}</p>
              <p v-if="item.print_spec" class="text-sm">{{ item.print_spec.name }}</p>
              <p v-if="item.customer_reference" class="text-sm">Reference: <em>{{ item.customer_reference }}</em></p>
            </td>
            <td class="text-center p-4">x{{ item.quantity }}</td>
            <td class="text-center p-4">{{ item.item_quote | gbp }}</td>
            <td class="text-center p-4">
              {{ item.created_at | date }}
              <small class="text-grey-darker block">{{ item.created_at | time }}</small>
            </td>
            <td class="text-center p-4">
              <i @click="viewItem(item)" class="far fa-edit fa-fw fa-lg cursor-pointer" />
            </td>
            <td class="text-center p-4">
              <i @click="showDeleteModal = true; selectedItem = item" class="fas fa-trash-alt fa-lg cursor-pointer" />
              <deletion-modal
                v-if="showDeleteModal"
                @confirmed="deleteItem(selectedItem)"
                @leave="showDeleteModal = false"
                @dismissed="showDeleteModal = false">
                <p class="text-center">Are you sure you wish to delete the saved print item?</p>
              </deletion-modal>
            </td>
            <td class="text-center pl-4 pr-6 py-4 whitespace-no-wrap border-r-8 border-grey-light">
              <loader v-if="updatingItem" />
              <button v-else @click="addToBasket(item)" class="px-3 py-3 rounded shadow bg-print-order-teal hover:bg-print-order-teal-dark text-sm text-white whitespace-no-wrap leading-none">
                Add to Basket <i class="fas fa-shopping-cart fa-fw ml-1" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { v2Api } from '@/http'
import { isEmpty } from 'lodash'
import { mapState, mapActions } from 'vuex'
import DeletionModal from '@/components/Modals/DeletionModal'
import PrintSubMenu from './print-order-form/PrintSubMenu'
export default {
  components: {
    PrintSubMenu,
    DeletionModal
  },
  data () {
    return {
      pageLoaded: false,
      searchLoaded: false,
      savedItems: [],
      officeAccess: true,
      searchParams: {
        q: null
      },
      selectedItem: null,
      showDeleteModal: false,
      updatingItem: false
    }
  },
  computed: {
    ...mapState(['selectedOffice', 'profile'])
  },
  watch: {
    selectedOffice: {
      handler () {
        this.loadSavedItems()
      },
      deep: true
    },
    searchParams: {
      handler () {
        this.loadSavedItems()
      },
      deep: true
    }
  },
  created () {
    Promise.all([
      this.loadSavedItems()
    ]).then(() => {
      this.pageLoaded = true
    })
  },
  methods: {
    isEmpty,
    ...mapActions([
      'displayToastNotification'
    ]),
    loadSavedItems () {
      this.searchLoaded = false
      return v2Api.get('custom-print-order-items', {
        params: {
          office_id: this.selectedOffice.id,
          status_id: 0, // 'Saved' status
          ...this.searchParams
        }
      }).then((response) => {
        this.savedItems = response.data.items
        this.officeAccess = response.data.access
      }).catch(() => {
        this.officeAccess = false
      }).finally(() => {
        this.searchLoaded = true
      })
    },
    deleteItem (item) {
      return v2Api.delete(`custom-print-order-items/${item.id}`)
        .then(() => {
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check',
            title: 'Item Deleted',
            info: 'Saved print item has been successfully deleted.'
          })
          this.loadSavedItems()
          this.$refs.printSubMenuRef.loadBadgeCounts()
        }).catch(({ response }) => {
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-exclamation',
            title: 'Error',
            info: response.data.message
          })
        }).finally(() => {
          this.showDeleteModal = false
        })
    },
    addToBasket (item) {
      this.updatingItem = true
      let params = {
        add_to_basket: true // Change item to 'Basket' status
      }

      v2Api.patch(`/custom-print-order-items/${item.id}`, params)
        .then(() => {
          this.$router.push('/print-order-form/basket')
        }, (errors) => {
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-exclamation',
            title: 'Error',
            info: errors.response.data.message
          })
        }).finally(() => {
          this.updatingItem = false
        })
    },
    viewItem (item) {
      this.$router.push(`/print-order-form/saved-items/${item.id}`)
    }
  }
}
</script>
